import * as React from 'react';
import { Col, Row } from '../row';
import { Label } from '../label/Label';
import { Button } from '../button/Button';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { BaseProps } from '../common/props';
import { useEffect, useState } from 'react';
import {
  formatTimeInputValue,
  parseTimeStrValueToISO,
  parseValueToTime,
} from './TimeInput';

export type TimePickerProps = BaseProps & {
  onlyTime?: boolean;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onClose?: () => void;
  onBlur?: (value?: string) => void;
  setHoursPartial: (value?: number) => void;
  setMinutesPartial: (value?: number) => void;
};
export const TimePicker: React.FC<TimePickerProps> = ({
  value,
  className,
  onlyTime,
  onClose,
  onChange,
  onBlur,
  setHoursPartial,
  setMinutesPartial,
  ...other
}) => {
  const hourSeparatorIndex = 1;
  const minuteSeparatorIndex = 0;

  const [hours, setHours] = useState<number>();
  const [minutes, setMinutes] = useState<number>();
  const [manualInput, setManualInput] = useState<boolean[]>([false, false]);

  useEffect(() => {
    //useEffect pro nastavení init hodnot
    if (!value) {
      setHours(undefined);
      setMinutes(undefined);
    } else {
      const formatTime = formatTimeInputValue(value, onlyTime);
      const [hoursNew, minutesNew] = formatTime
        .split(':')
        .map((time) => parseInt(time, 10));

      setHours(hoursNew);
      setMinutes(minutesNew);
    }
    setManualInput([false, false]);
  }, [value, onlyTime]);

  const setToForm = (time: string, onlyTime?: boolean) => {
    let result;
    if (onlyTime) {
      result = parseValueToTime(time);
    } else {
      result = parseTimeStrValueToISO(time);
    }

    onChange?.(result);
    onBlur?.(result);
  };

  useEffect(() => {
    //useEffect pro nastavení hodnoty do formuláře
    if (manualInput[0] && manualInput[1]) {
      setToForm(`${hours}:${minutes}`, onlyTime);
      onClose?.();
    }
  }, [hours, minutes, manualInput, onClose, onlyTime]);

  const generateGrid = (rows: number, columns: number, step: number) => {
    return Array.from({ length: rows }, (_, rowIndex) =>
      Array.from(
        { length: columns },
        (_, colIndex) => rowIndex * step + colIndex * rows * step
      )
    );
  };

  return (
    <div className={classNames(Classes.TIME_PICKER, className)} {...other}>
      <Row>
        <Col xs={'auto'}>
          <Label className={Classes.TIME_PICKER_LABEL}>Hodiny</Label>
          <div className={Classes.TIME_PICKER_HOURS_WRAPPER}>
            {generateGrid(6, 4, 1).map((hourRow) => (
              <div
                key={`${hourRow[0]}_${hourRow[hourRow.length]}`}
                className={Classes.TIME_PICKER_HOUR_ROW}
              >
                {hourRow.map((hour, index) => (
                  <React.Fragment key={hour}>
                    <div
                      className={classNames(Classes.TIME_PICKER_HOUR, {
                        [Classes.TIME_PICKER_ACTIVE]: hour === hours,
                      })}
                      onClick={() => {
                        //pokud je některá hodnota prázdná, tak se nastaví i parenrentské reference hodin
                        if (!manualInput.every((value) => value)) {
                          setHoursPartial(hour);
                        }

                        //nastavení hodin a příznaku, že byla hodnota manuálně změněna
                        setHours(hour);
                        setManualInput((value) => [true, value[1]]);
                      }}
                    >
                      {hour}
                    </div>
                    {index === hourSeparatorIndex && (
                      <div className={Classes.TIME_PICKER_COL_SEPARATOR} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </Col>
        <Col xs={'auto'}>
          <Label className={Classes.TIME_PICKER_LABEL}>Minuty</Label>
          <div className={Classes.TIME_PICKER_MINUTES_WRAPPER}>
            {generateGrid(6, 2, 5).map((minoutRow) => (
              <div
                key={`${minoutRow[0]}_${minoutRow[minoutRow.length]}`}
                className={Classes.TIME_PICKER_MINUTE_ROW}
              >
                {minoutRow.map((minute, index) => (
                  <React.Fragment key={minute}>
                    <div
                      className={classNames(Classes.TIME_PICKER_MINUTE, {
                        [Classes.TIME_PICKER_ACTIVE]: minute === minutes,
                      })}
                      onClick={() => {
                        //pokud je některá hodnota prázdná, tak se nastaví i parenrentské reference minut
                        if (!manualInput.every((value) => value)) {
                          setMinutesPartial(minute);
                        }

                        //nastavení minut a příznaku, že byla hodnota manuálně změněna
                        setMinutes(minute);
                        setManualInput((value) => [value[0], true]);
                      }}
                    >
                      {minute.toString().padStart(2, '0')}
                    </div>
                    {index === minuteSeparatorIndex && (
                      <div className={Classes.TIME_PICKER_COL_SEPARATOR} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </Col>
      </Row>
      <Button
        style={'link'}
        onClick={() => {
          setMinutesPartial(undefined);
          setHoursPartial(undefined);

          const today = new Date();
          setHours(today.getHours());
          setMinutes(today.getMinutes());
          setManualInput([true, true]);
        }}
      >
        Aktuální čas
      </Button>
    </div>
  );
};
